import { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAccount } from '../../contexts/Account';
import ACSnackbar from '../../components/ACSnackbar';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import {
  aobfStep5Mobile,
} from '../../services/company_services';

const styles = () => ({
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
});

function CompanyOnboardingStep5() {
  const { accountDetails, asLoggedUserDetails, editAccountInformation } =
    useAccount();
  const history = useHistory();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = styles();

  const [storyAndMission, setStoryAndMission] = useState(
    accountDetails.storyAndMission || ''
  );
  const [website, setWebsite] = useState(accountDetails.website || '');
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const disableButton = () => isLoading || !storyAndMission.trim();

  async function sendFormData() {
    try {
      await aobfStep5Mobile({
        CONTACT_ID: asLoggedUserDetails.userId,
        STORY_AND_MISSION: storyAndMission.trim(),
        WEBSITE: website.trim(),
      });

      return {
        storyAndMission: storyAndMission.trim(),
        website: website.trim(),
      };
    } catch (err) {
      console.log(err);
      setOpenSnackbar(true);
      return {};
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);

      const [payload] = await Promise.all([
        sendFormData(),
      ]);

      editAccountInformation({ ...payload });
      history.push('/company/onboarding/step6');
    } catch (error) {
      setIsLoading(false);
      setOpenSnackbar(true);
    }
  };

  const plannedUsage = accountDetails.plannedUsage || 'your board';

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
            }}
          >
            Setup
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
            }}
          >
            Step 5/6
          </Typography>
        </Grid>
        <LinearProgress
          variant="determinate"
          value={(5 / 6) * 100}
          sx={{
            height: 8,
            borderRadius: 999,
            backgroundColor: '#E8EDF4',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              backgroundColor: '#2F6CFB',
            },
          }}
        />
      </Box>

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '20px',
          lineHeight: '28px',
          textAlign: 'center',
          letterSpacing: '-0.5px',
          color: '#000000',
          mb: 1,
        }}
      >
        A little more about your {plannedUsage}
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          textAlign: 'center',
          letterSpacing: '-0.43px',
          color: '#646D7A',
          mb: 3,
        }}
      >
        Provide a short 2-3 sentence description of your {plannedUsage} so
        advisors who join your board can familiarize themselves.
      </Typography>

      <Grid>
        <Grid {...classes.gridItemProps} mb="16px">
          <TextField
            fullWidth
            multiline
            minRows={5}
            maxRows={8}
            placeholder="e.g. Acme Inc. is a distribution company founded in 2013. It is well positioned for international growth, and is looking to massively scale operations by the end of this year."
            value={storyAndMission}
            onChange={(e) => setStoryAndMission(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#F9FAFB',
                borderRadius: '14px',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                },
                '&:hover fieldset': {
                  borderColor: '#CBD5E1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3171F6',
                  boxShadow: '0 0 0 2px rgba(49, 113, 246, 0.15)',
                },
              },
            }}
            inputProps={{ maxLength: 500 }}
          />
        </Grid>

        <Grid {...classes.gridItemProps} mb="16px">
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '22px',
              color: '#111827',
              mb: 1,
            }}
          >
            Do you have a website?
          </Typography>
          <TextField
            fullWidth
            placeholder="www.acmeinc.com"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#F9FAFB',
                borderRadius: '14px',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                },
                '&:hover fieldset': {
                  borderColor: '#CBD5E1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3171F6',
                  boxShadow: '0 0 0 2px rgba(49, 113, 246, 0.15)',
                },
              },
            }}
            inputProps={{ maxLength: 150 }}
          />
        </Grid>

        <Grid
          {...classes.gridItemProps}
          md={5}
          display="flex"
          justifyContent="center"
        >
          <Button
            type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={disableButton()}
          sx={{
            borderRadius: '15px',
            backgroundColor: '#3171F6',
            '&:hover': {
              backgroundColor: '#2A60E0',
            },
            height: '52px',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 700,
            width: '100%',
            mb: isSM ? '24px' : null,
            my: '20px',
          }}
        >
          {isLoading ? <LoadingLogoSkeleton /> : 'Last Step'}
          </Button>
        </Grid>
      </Grid>
      <ACSnackbar
        onClose={() => setOpenSnackbar(false)}
        open={openSnackbar}
        severity="error"
        text="Something went wrong"
      />
    </Box>
  );
}

export default CompanyOnboardingStep5;
